import type { FC } from 'react';
import { useRef, useState, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import Slider from '@wix/da-ds/pkg/Slider';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Url } from '@wix/da-url';
/* TODO: replace FeaturedListItem with PostCompactCard once T69600 is done */
import FeaturedListItem from '../../../../../Sections/PostsSection/_partials/FeaturedList/FeaturedListItem';
import s from './PostsSlider.scss';

export interface Props {
  posts: PapiDeviation[];
  profileOwnerUsername: string;
  itemsPerFetch: number;
  className?: string;
  onPostClick?: (post: PapiDeviation) => void;
}

const CENTERED_SLIDER_MIN_WIDTH = 1300;
const RESIZE_THROTTLE_DELAY = 300;
const RESIZE_THROTTLE_OPTIONS = { trailing: true };

const PostsSlider: FC<Props> = ({
  posts,
  profileOwnerUsername,
  itemsPerFetch,
  className,
}) => {
  const isMobile = useContext(MobileContext);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation();

  const sliderRef = useRef<HTMLDivElement>(null);
  const [sliderWidth, setSliderWidth] = useState(
    sliderRef.current?.getBoundingClientRect().width
  );
  const [sliderHeight, setSliderHeight] = useState(
    sliderRef.current?.getBoundingClientRect().height
  );

  const handleResize = useThrottle(
    () => {
      if (!sliderRef.current) return;
      const newWidth = sliderRef.current.getBoundingClientRect().width;
      if (newWidth !== sliderWidth) {
        setSliderWidth(newWidth);
      }
      const newHeight = sliderRef.current.getBoundingClientRect().height;
      if (newHeight !== sliderHeight) {
        setSliderHeight(newHeight);
      }
    },
    [sliderWidth, sliderHeight],
    RESIZE_THROTTLE_DELAY,
    RESIZE_THROTTLE_OPTIONS
  );
  useResizeObserver(sliderRef, handleResize);

  const handleIndicatorClick = (index: number) => {
    setSelectedIndex(index);
  };

  if (!posts.length) {
    return null;
  }

  const displayPosts = posts.slice(0, itemsPerFetch);
  const hasMore = posts.length > itemsPerFetch;
  const totalSlides = displayPosts.length + (hasMore ? 1 : 0);
  const viewAllUrl = Url.to(
    `https://www.deviantart.com/${profileOwnerUsername}/posts`
  );

  const shouldCenterSlider =
    !isMobile &&
    totalSlides < 4 &&
    sliderWidth &&
    sliderWidth >= CENTERED_SLIDER_MIN_WIDTH;

  return (
    <div className={classnames(s['root'], className)} ref={sliderRef}>
      <Slider
        className={s['slider']}
        contentClassName={classnames(
          s['slider-content'],
          shouldCenterSlider && s['centered-slider-content']
        )}
        scrollToIndex={selectedIndex}
        scrollSnap={isMobile}
      >
        {displayPosts.map(post => (
          <div key={post.deviationId} className={s['post-card']}>
            <FeaturedListItem item={post} className={s['featured-item']} />
          </div>
        ))}
        <div key="view-all" className={s['post-card']}>
          <a href={viewAllUrl} className={s['view-all-card']}>
            <div className={s['view-all-content']}>
              <span className={s['view-all-text']}>{t('common.viewAll')}</span>
            </div>
          </a>
        </div>
      </Slider>

      {isMobile && totalSlides > 1 && (
        <div className={s['indicators']}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <div
              key={index}
              className={classnames(
                s['indicator'],
                index === selectedIndex && s['active']
              )}
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

PostsSlider.displayName = 'PostsSlider';

export default PostsSlider;
